import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Modal } from "../../../admin/react/common/modal";

// Review/edit UI for the AI "Generate Alt Text" flow. Presentational: it renders
// the current generation state and reports user intent (regenerate / accept /
// dismiss / edit) upward. No network here.
//
// Built on the shared admin Modal (native <dialog>) so we inherit focus trapping,
// Escape/backdrop close, the header "X", and a11y wiring for free. The design's
// footer maps onto the Modal's buttons: Regenerate -> tertiary, Dismiss -> cancel,
// Accept -> ok.

let instanceCounter = 0;

export const AltTextModal = ({
  isOpen,
  imageUrl,
  status,
  suggestion,
  onSuggestionChange,
  onRegenerate,
  onAccept,
  onClose,
  error,
  charLimit,
  fieldClassName,
  modalBtnClasses,
}) => {
  const textareaRef = useRef(null);
  const idRef = useRef(`ai-alt-text-modal-${(instanceCounter += 1)}`);

  const baseId = idRef.current;
  const textareaId = `${baseId}-textarea`;
  const helperId = `${baseId}-helper`;
  const messageId = `${baseId}-message`;

  const isLoading = status === "loading";
  const isPreview = status === "preview";
  const hasError = status === "error" || status === "rateLimited";
  const count = (suggestion || "").length;

  // eslint-disable-next-line no-nested-ternary
  const liveRegionText = isLoading
    ? "Generating alternative text."
    : isPreview
      ? "Suggestion ready. Review and edit the alternative text."
      : "";
  const statusText = hasError ? error : liveRegionText;

  // Move focus to the editable text as soon as a suggestion is ready so users
  // can immediately edit it (WCAG 2.4.3 Focus Order).
  useEffect(() => {
    if (isOpen && isPreview && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen, isPreview]);

  const title = (
    <span className="ai-alt-text-modal-title">
      <i className="fa-solid fa-wand-magic-sparkles" aria-hidden="true" />
      <span>AI Alternative Text</span>
    </span>
  );

  return (
    <Modal
      isOpen={isOpen}
      className="ai-alt-text-modal"
      title={title}
      onRequestClose={onClose}
      onOk={onAccept}
      okButtonLabel="Accept"
      okButtonClassName={modalBtnClasses.ok}
      okButtonDisabled={!isPreview || count === 0}
      cancelButtonLabel="Dismiss"
      cancelButtonClassName={modalBtnClasses.cancel}
      tertiaryButtonLabel="Regenerate"
      tertiaryButtonClassName={modalBtnClasses.tertiary}
      tertiaryButtonDisabled={!isPreview}
      onTertiary={onRegenerate}
    >
      <div className="ai-alt-text-modal-body">
        {imageUrl && (
          <img className="ai-alt-text-modal-image" src={imageUrl} alt="" />
        )}

        <div
          className="ai-alt-text-modal-status sr-only"
          role="status"
          aria-live="polite"
        >
          {statusText}
        </div>

        {isLoading ? (
          <p className="ai-alt-text-modal-loading">
            <i className="fa fa-spinner fa-spin" aria-hidden="true" />
            Generating alternative text&hellip;
          </p>
        ) : (
          <>
            <div
              className={classNames("ai-alt-text-modal-field", fieldClassName)}
            >
              <div className="ai-alt-text-modal-field-header">
                <label htmlFor={textareaId} className="ai-alt-text-modal-label">
                  Suggested Alternative Text
                </label>
                {isPreview && (
                  <span
                    className="ai-alt-text-modal-counter"
                    aria-live="polite"
                  >
                    {count}/{charLimit}
                  </span>
                )}
              </div>
              <textarea
                id={textareaId}
                ref={textareaRef}
                className="ai-alt-text-modal-textarea"
                value={suggestion}
                maxLength={charLimit}
                rows={3}
                readOnly={hasError}
                aria-describedby={
                  hasError ? `${helperId} ${messageId}` : helperId
                }
                onChange={(event) => onSuggestionChange(event.target.value)}
              />
              <p id={helperId} className="ai-alt-text-modal-helper">
                <i className="fa fa-info-circle" aria-hidden="true" />
                {hasError
                  ? "Click Regenerate to try again."
                  : "Edit freely. Nothing is saved to your photo until you click Accept."}
              </p>
            </div>
            {hasError && (
              <p
                id={messageId}
                className={`ai-alt-text-modal-message${
                  status === "error" ? " error" : ""
                }`}
              >
                {error}
              </p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

AltTextModal.propTypes = {
  // Controls the modal open state.
  isOpen: PropTypes.bool,
  // Source for the previewed image (photo.preview_url).
  imageUrl: PropTypes.string,
  // Generation state: "loading" | "preview" | "error" | "rateLimited".
  status: PropTypes.string,
  // Current (editable) suggestion text.
  suggestion: PropTypes.string,
  // Called with the new value as the user edits the textarea.
  onSuggestionChange: PropTypes.func.isRequired,
  // Re-run generation (tertiary "Regenerate" button).
  onRegenerate: PropTypes.func.isRequired,
  // Commit the (possibly edited) suggestion to the caption (ok "Accept" button).
  onAccept: PropTypes.func.isRequired,
  // Close/dismiss the modal (Escape, backdrop, X, or Dismiss).
  onClose: PropTypes.func.isRequired,
  // Error / rate-limit copy shown in the body.
  error: PropTypes.string,
  // Max characters allowed for the alt text.
  charLimit: PropTypes.number,
  // Extra CSS class(es) for the textarea field wrapper (theme-specific,
  // e.g. "em-basic-input" to inherit emphasis form field styles).
  fieldClassName: PropTypes.string,
  // Theme-specific CSS classes for the modal's action buttons.
  modalBtnClasses: PropTypes.shape({
    ok: PropTypes.string,
    cancel: PropTypes.string,
    tertiary: PropTypes.string,
  }),
};

AltTextModal.defaultProps = {
  isOpen: false,
  imageUrl: null,
  status: "loading",
  suggestion: "",
  error: "",
  charLimit: 125,
  fieldClassName: null,
  modalBtnClasses: {},
};
